import { get } from 'lodash';
import { Spinner } from 'flowbite-react';
import React, { useRef, useState } from 'react';
import { HiOutlineUpload } from 'react-icons/hi';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { checkResumeContent } from 'src/apis/parseResume';
import { getPolicyForS3NonVideo, updateUserById } from 'src/apis/user';
import Container from 'src/components/shared/Container';
import { DRIVE_FONT_STYLE, BackLink, MobileActionBar, MobileHeader, PrimaryButton, useDriveFont } from 'src/components/placementDrives/shared';
import { useToast } from 'src/context/ToastProvider';
import { useUserContext } from 'src/context/UserProvider';
import { handleS3Upload } from 'src/utils/imageUpload';

const fileNameFromLink = (link?: string) => decodeURIComponent((link || '').split('/').pop() || '').replace(/^\d+[-_]/, '') || 'resume';

// Resume step before applying: guests (and anyone without a resume on their profile) upload one resume that goes to every company.
const PlacementDriveResumePage = () => {
	useDriveFont();
	const { driveId } = useParams();
	const navigate = useNavigate();
	const toast = useToast();
	const queryClient = useQueryClient();
	const { user } = useUserContext();
	const inputRef = useRef<HTMLInputElement>(null);
	const [uploading, setUploading] = useState(false);
	const resumeLink = user?.profile?.resumeLink && user.profile.resumeLink !== 'empty' ? user.profile.resumeLink : '';

	const saveFn = useMutation(updateUserById, {
		onSuccess() {
			queryClient.refetchQueries(['verifyCookie']);
			setUploading(false);
			toast({ type: 'success', title: 'Resume saved' });
		},
		onError(error: Error) {
			setUploading(false);
			toast({ type: 'error', title: get(error, ['response', 'data', 'message'], error.message) });
		},
	});

	const onFile = async (file?: File) => {
		if (!file || !user) return;
		if (file.size > 5 * 1024 * 1024) return toast({ type: 'error', title: 'File size exceeds the maximum limit of 5 MB' });
		setUploading(true);
		try {
			const policy = await getPolicyForS3NonVideo(file.name, 'resumeUrls', file.type, true);
			const url = await handleS3Upload(policy, file);
			const { error } = await checkResumeContent(url || '', String(user._id));
			if (!url || error) {
				setUploading(false);
				return toast({ type: 'error', title: 'Resume is not valid' });
			}
			saveFn.mutate({ id: String(user._id), data: { profile: { resumeLink: url } } });
		} catch (e) {
			setUploading(false);
			toast({ type: 'error', title: 'Upload failed, please try again' });
		}
	};

	const goOn = () => navigate(`/placement-drives/${driveId}/apply`);

	return (
		<Container className="dp-fade w-[92%] max-w-[760px] pt-0 pb-32 lg:pt-8 lg:pb-16" style={DRIVE_FONT_STYLE}>
			<MobileHeader title="Resume" onBack={() => navigate(`/placement-drives/${driveId}`)} />
			<BackLink className="hidden lg:inline-flex" onClick={() => navigate(`/placement-drives/${driveId}`)}>
				Back to drive
			</BackLink>

			<div className="mt-4 lg:mt-10 lg:text-center">
				<h1 className="text-[28px] leading-[34px] lg:text-[48px] lg:leading-[52px] font-[800] tracking-[-0.035em] text-[#0F1626]">Add your resume</h1>
				<p className="mt-2.5 text-[14px] leading-[21px] lg:text-[17px] lg:leading-[26px] text-[#555770]">This one resume goes to every company you apply to.</p>
			</div>

			<input ref={inputRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={e => onFile(e.target.files?.[0])} />

			<div className="mt-7 lg:mt-10">
				{resumeLink ? (
					<div className="flex items-center gap-3.5 rounded-[16px] border border-[#DCD9FA] bg-[#EEEDFD] px-4 py-3.5 lg:px-5 lg:py-4">
						<div className="grid h-12 w-10 shrink-0 place-items-center rounded-[10px] bg-white text-[9px] font-[800] text-[#B42318] shadow-[0_1px_3px_rgba(20,20,60,0.1)]">{/\.docx?$/i.test(resumeLink) ? 'DOC' : 'PDF'}</div>
						<div className="min-w-0 flex-1">
							<p className="truncate text-[14px] font-[600] text-[#0F1626]">{fileNameFromLink(resumeLink)}</p>
							<p className="mt-0.5 text-[11.5px] font-[600] text-[#8A8BA3]">Saved to your profile</p>
						</div>
						<button disabled={uploading} onClick={() => inputRef.current?.click()} className="px-1 py-1.5 text-[13px] font-[600] text-[#4F46E5]">
							{uploading ? <Spinner size="sm" /> : 'Replace'}
						</button>
					</div>
				) : (
					<button
						disabled={uploading}
						onClick={() => inputRef.current?.click()}
						onDragOver={e => e.preventDefault()}
						onDrop={e => {
							e.preventDefault();
							onFile(e.dataTransfer.files?.[0]);
						}}
						className="w-full rounded-[16px] border-2 border-dashed border-[#D9D9E6] bg-[#F6F6FA] p-[30px] text-center transition hover:border-[#4F46E5] hover:bg-[#EEEDFD] lg:py-14"
					>
						<span className="mx-auto mb-3.5 grid h-[52px] w-[52px] place-items-center rounded-[16px] bg-white text-[#4F46E5] shadow-[0_1px_3px_rgba(20,20,60,0.1)]">
							{uploading ? <Spinner size="sm" /> : <HiOutlineUpload className="h-6 w-6" />}
						</span>
						<p className="text-[15px] font-[700] tracking-[-0.015em] text-[#0F1626]">{uploading ? 'Uploading...' : 'Upload your resume'}</p>
						<p className="mt-1.5 text-[13px] text-[#555770]">PDF or DOCX · up to 5 MB</p>
						<span className="mt-4 inline-block rounded-[11px] border border-[#E3E3EC] bg-white px-4 py-2.5 text-[13.5px] font-[700] text-[#0F1626] shadow-[0_1px_3px_rgba(20,20,60,0.08)]">Choose file</span>
					</button>
				)}
			</div>

			<div className="mt-8 hidden lg:flex lg:justify-center">
				<PrimaryButton className="!px-10 !py-3.5" disabled={!resumeLink || uploading} onClick={goOn}>
					Save and continue
				</PrimaryButton>
			</div>
			<MobileActionBar>
				<PrimaryButton className="w-full !py-4 !text-[16px]" disabled={!resumeLink || uploading} onClick={goOn}>
					Save and continue
				</PrimaryButton>
			</MobileActionBar>
		</Container>
	);
};

export default PlacementDriveResumePage;
